import moment, {type Moment} from 'moment-timezone';
import numeral from 'numeral';
import i18next from 'i18next';

import {parseDecimalToFloat} from 'web-app/util/parsers';
import {type FundingGrantListItemFragment} from 'web-app/react/invoicing-profiles/funding-grants/__generated__/queries.api-types';
import * as PlanConstants from 'web-app/react/entities/plans/constants';
import {hasValue} from 'web-app/util/typescript';

import * as Constants from './constants';

export const isFundingMethodManual = fundingMethod =>
    fundingMethod === PlanConstants.PUBLIC_FUNDING_TYPE.MANUAL ||
    fundingMethod === PlanConstants.PUBLIC_FUNDING_TYPE.MANUAL_BY_RATE;

export const bestFundingMethod = (avaliableFundingMethods: PlanConstants.PUBLIC_FUNDING_TYPE[]) => {
    if (avaliableFundingMethods.length === 0) {
        return PlanConstants.PUBLIC_FUNDING_TYPE.NONE;
    } else if (avaliableFundingMethods.includes(PlanConstants.PUBLIC_FUNDING_TYPE.ASSISTED)) {
        return PlanConstants.PUBLIC_FUNDING_TYPE.ASSISTED;
    } else if (avaliableFundingMethods.includes(PlanConstants.PUBLIC_FUNDING_TYPE.MANUAL_BY_RATE)) {
        return PlanConstants.PUBLIC_FUNDING_TYPE.MANUAL_BY_RATE;
    } else if (avaliableFundingMethods.includes(PlanConstants.PUBLIC_FUNDING_TYPE.MANUAL)) {
        return PlanConstants.PUBLIC_FUNDING_TYPE.MANUAL;
    } else {
        return PlanConstants.PUBLIC_FUNDING_TYPE.UNKNOWN;
    }
};

export interface FundingType extends FundingGrantListItemFragment {
    __typename: 'FundingPlan';
}

export const formValuesToPublicFundingSettings = (
    formValues: any,
    fundingMethod: PlanConstants.PUBLIC_FUNDING_TYPE,
) => {
    if (!formValues) {
        return {};
    }

    const fundingTypeIds = [formValues.fundingType].filter(g => g);

    const duration = formValues.setAsHours
        ? {
              hours: parseDecimalToFloat(formValues.hours) || 0,
              minutes: null,
              maxFundedMinutes: formValues.maxFundedMinutes || null,
          }
        : {
              minutes: formValues.minutes || 0,
              hours: null,
              maxFundedMinutes: formValues.maxFundedMinutes || null,
          };

    if (isFundingMethodManual(fundingMethod)) {
        const period = formValues.frequency;
        return {
            ...duration,
            method: fundingMethod,
            amount: parseDecimalToFloat(formValues.amount) || 0,
            rate: period === PlanConstants.FREQUENCY.WEEKLY ? parseDecimalToFloat(formValues.rate) : null,
            period,
            fundingTypeIds,
        };
    } else {
        // If we hit the else case it means that we're doing assisted funding...
        return {
            ...duration,
            // ...and assisted funding is always weekly
            period: PlanConstants.FREQUENCY.WEEKLY,
            method: PlanConstants.PUBLIC_FUNDING_TYPE.ASSISTED,
            fundingTypeIds,
        };
    }
};

export const getActiveFundingTypePeriod = (fundingType: FundingType, activeDay: Moment) =>
    fundingType.periods.find(type => {
        const fromMoment = moment(type.from);
        const toMoment = type.to ? moment(type.to) : null;
        if (!toMoment && fromMoment.isSameOrBefore(activeDay, 'day')) {
            return true;
        } else if (
            toMoment &&
            toMoment.isSameOrAfter(activeDay, 'day') &&
            fromMoment.isSameOrBefore(activeDay, 'day')
        ) {
            return true;
        }
        return false;
    });

const formatRatePeriod = (ratePeriod: FundingType['periods'][0]) => {
    if (!ratePeriod) {
        return '';
    } else if (!ratePeriod.to) {
        return `${i18next.t('since')} ${moment(ratePeriod.from).format('LL')}: `;
    } else {
        return `${moment(ratePeriod.from).format('LL')} - ${moment(ratePeriod.to).format('LL')}: `;
    }
};

export const getFundingTypeSubtitle = (fundingType: FundingType, activeDay: Moment) => {
    const activePeriod = getActiveFundingTypePeriod(fundingType, activeDay);

    if (!activePeriod) {
        return undefined;
    }

    const claimable = activePeriod ? activePeriod.rates.reduce((sum, current) => sum + Number(current.rate), 0) : null;

    if (!claimable) {
        return undefined;
    }

    return `${formatRatePeriod(activePeriod)}${numeral(claimable).format('$0.[00]')}${i18next.t('time.perHoursAbbr')}`;
};

export const formatNumberForForm = num => numeral(num).format(Constants.NUMERAL_FORMAT);

export const isWithinMinuteRange = value =>
    hasValue(value) && (value < Constants.MIN_FUNDED_MINUTES_PER_DAY || value > Constants.MAX_FUNDED_MINUTES_PER_DAY)
        ? i18next.t('plans.publicFunding.maxFundedHoursError')
        : undefined;
